import React from 'react';
import { connect } from 'react-redux';
import { compose, withHandlers } from 'recompose';
import { Dispatch } from 'redux';
import chatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import {
  connectedPageSelector,
  formattedBotsSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ActivateInactiveBotModalPropsType } from '@mm/state/modules/modalsState/props';
import { Button } from '@mm/components';
import { HandlerMethodTypes } from '@mm/utils';
import { ModalBody, ModalFooter, ModalHeader } from '../modal-helpers';
import './ActivateInactiveBotModal.pcss';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type ExternalProps = ActivateInactiveBotModalPropsType &
  StoreProps &
  DispatchProps;

type HandlerProps = ExternalProps;
type HandlerMethods = HandlerMethodTypes<HandlerProps, typeof handlers>;

type InnerProps = HandlerMethods;

type Props = InnerProps & ExternalProps;

const mapStateToProps = (state: StoreState) => ({
  chatbot: state.chatbot.activeChatbot,
  connectedPage: connectedPageSelector(state),
  chatbots: formattedBotsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  attachToFacebookPage: (botId: number, facebookPageId: number) =>
    dispatch(chatbotCRUDActions.attachToFacebookPage(botId, facebookPageId)),
});

const onActivateBot =
  ({
    attachToFacebookPage,
    chatbot,
    connectedPage,
    closeModal = () => {},
    onBotActivateConfirm = () => {},
    useCustomOnBotActivateConfirm,
  }: HandlerProps) =>
  async () => {
    closeModal();
    onBotActivateConfirm();
    if (chatbot && connectedPage && !useCustomOnBotActivateConfirm) {
      await attachToFacebookPage(chatbot.id, connectedPage.id);
    }
  };

const handlers = {
  onActivateBot,
};

const enhance = compose<InnerProps, ActivateInactiveBotModalPropsType>(
  connect(mapStateToProps, mapDispatchToProps),
  withHandlers(handlers),
);

const ActivateInactiveBotModal = (props: Props) => {
  const {
    onClose = () => {},
    closeModal = () => {},
    onActivateBot,
    switchToAnotherBot,
    chatbots,
    connectedPage,
  } = props;

  const connectedChatbotId = connectedPage?.activeBotId;
  const connectedChatbot = chatbots.find(bot => {
    return bot.id === connectedChatbotId;
  });
  const botName = connectedChatbot?.name ?? '';

  const modalHeaderText = switchToAnotherBot
    ? 'Your Chatbot is Inactive!'
    : 'Inactive Chatbot';

  const modalBodyText = switchToAnotherBot ? (
    <div className="activate-inactive-bot-modal__body-instruction">
      There is another active bot on this page: {botName}. <br /> Do you want to
      pause that one and activate this bot instead?
    </div>
  ) : (
    <div className="activate-inactive-bot-modal__body-instruction">
      This Chatbot is currently paused. <br /> Activate it to use this feature!
    </div>
  );

  const activeButtonText = switchToAnotherBot
    ? 'Activate this bot'
    : 'Activate';

  const handleCloseModal = () => {
    closeModal();
    onClose();
  };

  return (
    <div className="activate-inactive-bot-modal">
      <ModalHeader
        className="activate-inactive-bot-modal__header"
        closeOverride={handleCloseModal}
      >
        {modalHeaderText}
      </ModalHeader>

      <ModalBody className="activate-inactive-bot-modal__body">
        <i className="fas fa-exclamation-triangle activate-inactive-bot-modal__body-triangle" />
        <div>{modalBodyText}</div>
      </ModalBody>

      <ModalFooter className="activate-inactive-bot-modal__footer">
        <Button
          color="green"
          css={{ container: 'activate-inactive-bot-modal__button' }}
          onClick={onActivateBot}
        >
          {activeButtonText}
        </Button>
        <Button
          color="transparent"
          css={{ container: 'activate-inactive-bot-modal__button' }}
          onClick={handleCloseModal}
        >
          Cancel
        </Button>
      </ModalFooter>
    </div>
  );
};

export default enhance(ActivateInactiveBotModal);
